import { useAuthState } from '@packages/contexts/auth';
import { fetchDashboard, fetchDashboardUpdates } from '@packages/contexts/dashboard/dashboard.context';
import { useDashboardDispatch, useDashboardState } from '@packages/contexts/dashboard/dashboard.hooks';
import { type IDashboardUpdates } from '@packages/contexts/dashboard/dashboard.state';
import { useFeatureFlags } from '@packages/contexts/feature-flags';
import { usePermissionsState } from '@packages/contexts/permissions';
import { useQmrTableActions } from '@packages/contexts/qmrs';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { useAbortController } from '@packages/core/http';
import colors from '@packages/core/styles/colors';
import { type Qmr, type SavedSearch } from '@packages/models/api';
import {
    AlertBar,
    Button,
    Icon,
    isValidIconType,
    type StsIconName,
    TextBadge,
    Typography,
    useAlert,
} from '@packages/ui/shared';
import AsyncPage from '@web/components/async-page';
import { ContinueToVinSessionModal } from '@web/components/continue-to-vin-session-modal';
import { DetailView, DetailViewBody, DetailViewContent } from '@web/components/detail-view';
import { MenuTable } from '@web/components/menu-table';
import { SecurityCodeBanner, useSecurityCodeNotification } from '@web/components/security-code-banner';
import { StisSubscriptionExpiryBanner } from '@web/components/stis-subscription-expiry-banner';
import { type SORT_DIRECTION } from '@web/components/table';
import { TableRenderer } from '@web/components/table';
import { QmrTableHead, QmrTableRow } from '@web/qmr/components/qmr-table';
import { analyticsService, dashboardService, qmrsService } from '@web/services/singletons';
import { sanitizeRichText } from '@web/utils';
import {
    canAccessSTIS,
    getStisAccessState,
    getSubaruDiagnosticsUrl,
    hasStisThirdPartyRole,
} from '@web/utils/can-view-stis';
import { usePublicationsState } from '@web/vin-sessions/components/stis-publications/context/publications.hooks';
import { useVinSessionState } from '@web/vin-sessions/context/vin-sessions.hooks';
import { type StisDocument } from '@web/vin-sessions/types';
import {
    fetchFavoritesTableData,
    fetchRecentlyViewedData,
    fetchWhatsNewData,
} from '@web/vin-sessions/utils/vinSessionUtils';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useNavigate } from 'react-router-dom';

const useStyles = createUseStyles({
    banner: {
        alignItems: 'center',
        backgroundColor: colors.blueOne,
        borderRadius: 8,
        display: 'flex',
        justifyContent: 'space-between',
        padding: 20,
    },
    card: {
        borderRadius: 8,
        boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.16)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'space-between',
        padding: 20,
    },
    menuCardContainer: {
        display: 'flex',
        gap: 10,
        justifyContent: 'space-between',
        maxWidth: '100%',
        overflow: 'hidden',
    },
    stisCardsWrapper: {
        position: 'relative',
    },
    stisCardBlocked: {
        width: '32%',
        '& > div': {
            width: '100% !important',
        },
    },
    stisCardOverlay: {
        position: 'absolute',
        top: 49,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.88)',
        backdropFilter: 'blur(4px)',
        gap: 8,
        padding: 16,
        textAlign: 'center',
    },
});

export type MenuProps = {
    icon: string;
    name: string;
};

type QmrTableRowModel = {
    rowId: string;
    checked: boolean;
} & Qmr;

const Overview: FC = () => {
    const navigate = useNavigate();
    const classes = useStyles();
    const { t } = useTranslation();
    const { account } = useAuthState();
    const { permissions } = usePermissionsState();
    const { isOtpwFlowFeatureEnabled } = useFeatureFlags();

    const hasPermission = useCallback(
        (ids: string[]) => permissions?.some((p: any) => ids.includes(p.permissionId)),
        [permissions]
    );
    const showContentSetting = useMemo(() => hasPermission(['DASHBOARD002']), [hasPermission]);
    const canViewQmrs = Boolean(account?.systemCapabilities?.canViewQualityAssurance);

    const [savedSearch, setSavedSearch] = useState<Partial<SavedSearch>>();
    const [qmrTableRows, setQmrTableRows] = useState<QmrTableRowModel[]>([]);
    const [startCount, setStartCount] = useState('0');
    const [endCount, setEndCount] = useState('0');
    const [qmrTotal, setQmrTotal] = useState('0');
    const [selectAll, setSelectAll] = useState(false);
    const [selectedQmrs, setSelectedQmrs] = useState<QmrTableRowModel[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [sortBy, setSortBy] = useState('');
    const [sortDirection, setSortDirection] = useState('');
    const { isVinLoading, selectedVin } = useVinSessionState();
    const [favoritePublications, setFavoritePublications] = useState<StisDocument[]>([]);
    const [recentlyViewedPublications, setRecentlyViewedPublications] = useState<StisDocument[]>([]);
    const [whatsNewPublications, setWhatsNewPublications] = useState<StisDocument[]>([]);

    const { canAccessStis } = usePublicationsState();

    const stisAccessState = getStisAccessState(account);
    const isStisBlocked =
        stisAccessState === 'EXPIRED' || stisAccessState === 'PENDING' || stisAccessState === 'NO_SUBSCRIPTION';

    const [vinSessionModalOpen, setVinSessionModalOpen] = useState(false);
    const securityCodeData = useSecurityCodeNotification();

    useEffect(() => {
        const handleOpenAuthModal = () => {
            if (isOtpwFlowFeatureEnabled && securityCodeData.hasActiveSecurityCode) {
                setVinSessionModalOpen(true);
            }
        };
        window.addEventListener('openAuthorizationCodeModal', handleOpenAuthModal);
        return () => {
            window.removeEventListener('openAuthorizationCodeModal', handleOpenAuthModal);
        };
    }, [isOtpwFlowFeatureEnabled, securityCodeData.hasActiveSecurityCode]);

    const dashboardState = useDashboardState();
    const dashboardDispatch = useDashboardDispatch();
    const { abortSignalRef } = useAbortController();

    const alert = useAlert();

    const qmrTableActions = useQmrTableActions({
        qmrs: selectedQmrs.length ? selectedQmrs : null,
        alert,
        qmrsService,
    });

    const handleSortChange = (sortBy: string, sortDirection: SORT_DIRECTION) => {
        setSortBy(sortBy);
        setSortDirection(sortDirection);
        fetchQmrList(sortBy, sortDirection);
    };

    const fetchQmrList = async (sortBy?: string, sortDirection?: SORT_DIRECTION) => {
        if (!canViewQmrs) {
            setQmrTableRows([]);
            setStartCount('0');
            setEndCount('0');
            setQmrTotal('0');
            return;
        }

        setIsLoading(true);
        try {
            let queryParams: any = { size: '10', qmrStatusId: 'DRAFT' };
            if (sortBy && sortDirection) {
                queryParams = { ...queryParams, sortOrder: sortBy, sortDir: sortDirection };
            }
            const queryString = new URLSearchParams(queryParams).toString();
            const response = await qmrsService.fetchQmrsList({ queryString });
            setIsLoading(false);
            if (response.success) {
                setQmrTableRows(
                    response.data.qmrs.map((qmr) => {
                        const qmrAsTableRow = { ...qmr, rowId: qmr.qmrId, checked: false };
                        return qmrAsTableRow;
                    })
                );
                setStartCount(response.data.displayStartCountDescription);
                setEndCount(response.data.displayEndCountDescription);
                setQmrTotal(response.data.totalCountDescription);
            } else {
                throw response.data;
            }
        } catch (error) {
            window.alert(typeof error.message === 'string' ? error.message : 'Something went wrong.');
        }
    };

    const fetchData = useCallback(async () => {
        await fetchDashboard({
            dashboardDispatch,
            dashboardService,
            signal: abortSignalRef.current,
        });

        fetchDashboardUpdates({
            dashboardDispatch,
            dashboardService,
            signal: abortSignalRef.current,
        });

        if (!account) {
            return;
        }

        if (account.defaultSavedSearchId) {
            try {
                const dashboardQmrParams = new URLSearchParams({ size: '10' }).toString();
                const response = await qmrsService.getAdvancedSearchResultsForSearchId(
                    account.defaultSavedSearchId,
                    dashboardQmrParams
                );
                if (response.success) {
                    setSavedSearch(response.data.savedSearch);
                    setQmrTableRows(
                        response.data.results.qmrs.map((qmr) => {
                            const qmrAsTableRow = { ...qmr, rowId: qmr.qmrId, checked: false };
                            return qmrAsTableRow;
                        })
                    );
                    setStartCount(response.data.results.displayStartCountDescription);
                    setEndCount(response.data.results.displayEndCountDescription);
                    setQmrTotal(response.data.results.totalCountDescription);
                } else {
                    throw response.data;
                }
            } catch (error) {
                window.alert(typeof error.message === 'string' ? error.message : 'Something went wrong.');
            }
        } else {
            // set saved search to default state if no saved search is found (in the case of user had a saved search and then it was removed)
            setSavedSearch(undefined);
            fetchQmrList();
        }
    }, [abortSignalRef, account, canViewQmrs, dashboardDispatch]);

    function handleStartQmrButtonClick() {
        analyticsService.logEvent(ANALYTICS_EVENTS.USER_CLICKS_CREATE_A_QMR);
        navigate('/qmrs', { state: { startNew: true } });
    }

    useEffect(() => {
        const fetchDashboardData = async () => {
            if (account) {
                await fetchFavoritesTableData(setFavoritePublications, setIsLoading, selectedVin);

                await fetchWhatsNewData(setWhatsNewPublications, setIsLoading);

                await fetchRecentlyViewedData(setRecentlyViewedPublications, setIsLoading, selectedVin);
            }
        };

        fetchDashboardData();
    }, [account]);

    if (isVinLoading) {
        return (
            <div style={{ height: '80%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Spinner animation={'border'} style={{ height: 72, width: 72, color: '#1F518F' }} />
            </div>
        );
    }

    function handleEditContent() {
        navigate('/administration/content-settings/techshare');
    }

    return (
        <AsyncPage fetchData={fetchData}>
            <DetailView>
                <AlertBar
                    error={alert.type === 'error'}
                    onClose={alert.close}
                    show={alert.isOpen}
                    success={alert.type === 'success'}
                >
                    <Typography>{alert.content}</Typography>
                </AlertBar>
                <DetailViewContent topOffset="none">
                    <DetailViewBody>
                        <Row className="mb-6">
                            <Col>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="h1">
                                            {t('views:dashboard.header.hello', `Hi ${account?.firstName}`)}
                                        </Typography>
                                        <Typography color="graySix" style={{ marginLeft: 12 }} variant="h2">
                                            {t('views:dashboard.header.message', 'Welcome back!')}
                                        </Typography>
                                    </div>
                                    {showContentSetting && (
                                        <Button onPress={handleEditContent} variant="primary">
                                            Edit Content
                                        </Button>
                                    )}
                                </div>
                            </Col>
                        </Row>

                        {isOtpwFlowFeatureEnabled && securityCodeData.activeVins.length > 0 && (
                            <ContinueToVinSessionModal
                                onHide={() => setVinSessionModalOpen(false)}
                                show={vinSessionModalOpen}
                                vins={securityCodeData.activeVins}
                            />
                        )}

                        <StisSubscriptionExpiryBanner />

                        {!hasStisThirdPartyRole(account) &&
                            dashboardState.dashboard &&
                            (dashboardState.dashboard.bannerHeader || dashboardState.dashboard.bannerMessage) && (
                                <Row className="mb-8">
                                    <Col>
                                        <div className={classes.banner}>
                                            <div>
                                                <div className="mb-1">
                                                    {dashboardState.dashboard.bannerHeader && (
                                                        <Typography color="white" variant="h3">
                                                            {dashboardState.dashboard.bannerHeader}
                                                        </Typography>
                                                    )}
                                                </div>
                                                {dashboardState.dashboard.bannerMessage && (
                                                    <div style={{ paddingRight: '1em' }}>
                                                        <Typography color="white">
                                                            <span
                                                                style={{ whiteSpace: 'pre-wrap' }}
                                                                dangerouslySetInnerHTML={{
                                                                    __html: sanitizeRichText(
                                                                        dashboardState.dashboard.bannerMessage
                                                                    ),
                                                                }}
                                                            ></span>
                                                        </Typography>
                                                    </div>
                                                )}
                                            </div>
                                            {dashboardState.dashboard.bannerLink && (
                                                <div className="d-flex align-items-center justify-content-center p-5">
                                                    <Button
                                                        iconRight={
                                                            <Icon color="blueOne" name="chevron-right" size={16} />
                                                        }
                                                        onPress={() => {
                                                            window.open(dashboardState.dashboard.bannerLink, '_blank');
                                                        }}
                                                        variant="info"
                                                    >
                                                        {t('buttons:readMore', 'Read More')}
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            )}

                        {isOtpwFlowFeatureEnabled &&
                            securityCodeData.hasActiveSecurityCode &&
                            securityCodeData.expiresAt && (
                                <SecurityCodeBanner
                                    buttonText="openVinSession"
                                    expiresAt={securityCodeData.expiresAt}
                                    onButtonClick={() => setVinSessionModalOpen(true)}
                                    showTimer={!securityCodeData.hasMultipleVins}
                                />
                            )}

                        {(canAccessStis || canAccessSTIS(account)) && (
                            <div className={`mb-2 ${classes.menuCardContainer}`}>
                                {isStisBlocked ? (
                                    <>
                                        <div className={classes.stisCardBlocked}>
                                            <MenuTable
                                                header="Favorites"
                                                navLink="/stis?filter=favorites"
                                                noDataSubText="Mark STIS Publications as favorites and get an easy access to them"
                                                noDataText="No favorites have been added"
                                                tableData={[]}
                                                blockedOverlay={
                                                    <div className={classes.stisCardOverlay}>
                                                        <Icon name="lock-closed" size={24} color="graySix" />
                                                        <Typography variant="caption">
                                                            {stisAccessState === 'EXPIRED'
                                                                ? 'Subscription expired'
                                                                : stisAccessState === 'PENDING'
                                                                  ? 'Payment pending'
                                                                  : 'Subscription required'}
                                                        </Typography>
                                                        <Button
                                                            variant="primary"
                                                            onPress={() =>
                                                                window.open(
                                                                    getSubaruDiagnosticsUrl(),
                                                                    '_blank',
                                                                    'noopener,noreferrer'
                                                                )
                                                            }
                                                            iconRight={
                                                                <Icon name="open-in-new" size={14} color="white" />
                                                            }
                                                        >
                                                            {stisAccessState === 'EXPIRED'
                                                                ? 'Renew Subscription'
                                                                : 'Subscribe'}
                                                        </Button>
                                                    </div>
                                                }
                                            />
                                        </div>
                                        <div className={classes.stisCardBlocked}>
                                            <MenuTable
                                                header={`What's New`}
                                                navLink="/stis?page=0&size=10"
                                                noDataSubText={`SOA hasn't uploaded any new publications for this specific car recently`}
                                                noDataText="No new publications have been uploaded"
                                                tableData={[]}
                                                blockedOverlay={
                                                    <div className={classes.stisCardOverlay}>
                                                        <Icon name="lock-closed" size={24} color="graySix" />
                                                        <Typography variant="caption">
                                                            {stisAccessState === 'EXPIRED'
                                                                ? 'Subscription expired'
                                                                : stisAccessState === 'PENDING'
                                                                  ? 'Payment pending'
                                                                  : 'Subscription required'}
                                                        </Typography>
                                                        <Button
                                                            variant="primary"
                                                            onPress={() =>
                                                                window.open(
                                                                    getSubaruDiagnosticsUrl(),
                                                                    '_blank',
                                                                    'noopener,noreferrer'
                                                                )
                                                            }
                                                            iconRight={
                                                                <Icon name="open-in-new" size={14} color="white" />
                                                            }
                                                        >
                                                            {stisAccessState === 'EXPIRED'
                                                                ? 'Renew Subscription'
                                                                : 'Subscribe'}
                                                        </Button>
                                                    </div>
                                                }
                                            />
                                        </div>
                                        <div className={classes.stisCardBlocked}>
                                            <MenuTable
                                                header="Recently Viewed"
                                                navLink="/stis?filter=recently-viewed"
                                                noDataSubText="Your last visited documents will automatically appear here"
                                                noDataText="No recently Viewed documents"
                                                tableData={[]}
                                                blockedOverlay={
                                                    <div className={classes.stisCardOverlay}>
                                                        <Icon name="lock-closed" size={24} color="graySix" />
                                                        <Typography variant="caption">
                                                            {stisAccessState === 'EXPIRED'
                                                                ? 'Subscription expired'
                                                                : stisAccessState === 'PENDING'
                                                                  ? 'Payment pending'
                                                                  : 'Subscription required'}
                                                        </Typography>
                                                        <Button
                                                            variant="primary"
                                                            onPress={() =>
                                                                window.open(
                                                                    getSubaruDiagnosticsUrl(),
                                                                    '_blank',
                                                                    'noopener,noreferrer'
                                                                )
                                                            }
                                                            iconRight={
                                                                <Icon name="open-in-new" size={14} color="white" />
                                                            }
                                                        >
                                                            {stisAccessState === 'EXPIRED'
                                                                ? 'Renew Subscription'
                                                                : 'Subscribe'}
                                                        </Button>
                                                    </div>
                                                }
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <MenuTable
                                            header="Favorites"
                                            navLink="/stis?filter=favorites"
                                            noDataSubText="Mark STIS Publications as favorites and get an easy access to them"
                                            noDataText="No favorites have been added"
                                            tableData={
                                                favoritePublications?.length > 0 ? favoritePublications.slice(0, 5) : []
                                            }
                                        />
                                        <MenuTable
                                            header={`What's New`}
                                            navLink="/stis?page=0&size=10"
                                            noDataSubText={`SOA hasn't uploaded any new publications for this specific car recently`}
                                            noDataText="No new publications have been uploaded"
                                            tableData={
                                                whatsNewPublications?.length > 0 ? whatsNewPublications.slice(0, 5) : []
                                            }
                                        />
                                        <MenuTable
                                            header="Recently Viewed"
                                            navLink="/stis?filter=recently-viewed"
                                            noDataSubText="Your last visited documents will automatically appear here"
                                            noDataText="No recently Viewed documents"
                                            tableData={
                                                recentlyViewedPublications?.length > 0
                                                    ? recentlyViewedPublications.slice(0, 5)
                                                    : []
                                            }
                                        />
                                    </>
                                )}
                            </div>
                        )}

                        {/* Future Work TS-2048*/}
                        {/* <DocumentSearch/> */}

                        {!hasStisThirdPartyRole(account) && (
                            <>
                                <Row className="mb-6">
                                    <Col>
                                        <Typography variant="h3">
                                            {t('views:dashboard.latestUpdates.title', 'Latest Updates')}
                                        </Typography>
                                    </Col>
                                </Row>

                                <Row className="mb-8">
                                    {dashboardState.dashboard && dashboardState?.dashboardUpdates?.length > 0 ? (
                                        <>
                                            {dashboardState.dashboardUpdates.map((update: IDashboardUpdates) => {
                                                return (
                                                    <Col xs={4}>
                                                        <div className={classes.card}>
                                                            <div>
                                                                <div className="mb-4">
                                                                    <div className="d-flex">
                                                                        <TextBadge
                                                                            icon={
                                                                                isValidIconType(
                                                                                    update.type as StsIconName
                                                                                ) ? (
                                                                                    <Icon
                                                                                        color="blueOne"
                                                                                        name={
                                                                                            update.type as StsIconName
                                                                                        }
                                                                                        size={14}
                                                                                        style={{ display: 'flex' }}
                                                                                    />
                                                                                ) : null
                                                                            }
                                                                            variant="blue"
                                                                        >
                                                                            {update.type}
                                                                        </TextBadge>
                                                                    </div>
                                                                </div>
                                                                <div className="mb-2">
                                                                    <Typography variant="h5">
                                                                        {update.heading}
                                                                    </Typography>
                                                                </div>
                                                                <div className={update.link ? 'mb-6' : 'mb-0'}>
                                                                    <Typography color="graySix" variant="labelRegular">
                                                                        {update.description}
                                                                    </Typography>
                                                                </div>
                                                            </div>
                                                            {update.link && (
                                                                <div className="d-flex">
                                                                    <Button
                                                                        iconRight={
                                                                            <Icon name="chevron-right" size={16} />
                                                                        }
                                                                        onPress={() => {
                                                                            window.open(update.link, '_blank');
                                                                        }}
                                                                        variant="tertiary"
                                                                    >
                                                                        {t('buttons:readMore', 'Read More')}
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Col>
                                                );
                                            })}
                                        </>
                                    ) : (
                                        <Col>
                                            <div className={classes.card}>
                                                <div className="mb-2">
                                                    <Typography variant="h5">
                                                        {t(
                                                            'views:dashboard.latestUpdates.noDataTitle',
                                                            'No updates available at this time.'
                                                        )}
                                                    </Typography>
                                                </div>
                                                <div className="mb-0">
                                                    <Typography color="graySix" variant="labelRegular">
                                                        {t(
                                                            'views:dashboard.latestUpdates.noDataDescription',
                                                            'Check back later.'
                                                        )}
                                                    </Typography>
                                                </div>
                                            </div>
                                        </Col>
                                    )}
                                </Row>
                            </>
                        )}

                        {canViewQmrs && (
                            <>
                                <Row className="mb-4">
                                    <Col>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center">
                                                <Typography variant="h3">
                                                    {savedSearch
                                                        ? savedSearch.name
                                                        : t('views:dashboard.customDisplay.title', 'My Drafts')}
                                                </Typography>
                                                {/* <Button
                                                    variant="link"
                                                    style={{ marginLeft: 32 }}
                                                    onPress={() => {
                                                        return;
                                                    }}
                                                >
                                                    {t('buttons:changeView', 'Change view')}
                                                </Button> */}
                                            </div>
                                            {(account?.systemCapabilities.createDraftQmr ||
                                                account?.systemCapabilities.createDraftQmrForAnyRetailer) && (
                                                <Button
                                                    iconRight={
                                                        <Icon
                                                            name="plus"
                                                            raised
                                                            raisedContainerStyle={{
                                                                marginLeft: 16,
                                                                width: 22,
                                                                height: 22,
                                                            }}
                                                            size={16}
                                                        />
                                                    }
                                                    onPress={handleStartQmrButtonClick}
                                                >
                                                    {t('buttons:startQmr')}
                                                </Button>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                                <Row className="mb-4">
                                    <Col style={{ minHeight: 200 }}>
                                        <TableRenderer<QmrTableRowModel>
                                            isLoading={isLoading}
                                            onSelectionChange={setSelectedQmrs}
                                            selectAll={selectAll}
                                            selectAllSetter={setSelectAll}
                                            tableBodyRowRenderer={(rowData, rowSelectHandler) => {
                                                return (
                                                    <QmrTableRow
                                                        checked={rowData.checked}
                                                        key={rowData.qmrId}
                                                        onCheckboxChange={rowSelectHandler}
                                                        qmr={rowData}
                                                    />
                                                );
                                            }}
                                            tableHeaderRowRenderer={(selectAllValue, selectAllChangeHandler) => {
                                                return (
                                                    <QmrTableHead
                                                        onActionPress={qmrTableActions.handleActionPress}
                                                        onSelectAllChange={selectAllChangeHandler}
                                                        onSort={handleSortChange}
                                                        selectAll={selectAllValue}
                                                        selectedItems={selectedQmrs}
                                                        selectedCount={selectedQmrs.length}
                                                        sortDirection={
                                                            sortDirection ? (sortDirection as SORT_DIRECTION) : null
                                                        }
                                                        sortOrder={sortBy}
                                                    />
                                                );
                                            }}
                                            tableRowsData={qmrTableRows}
                                            tableRowsDataSetter={setQmrTableRows}
                                        />
                                    </Col>
                                </Row>
                                <Row className="pb-4">
                                    <Col>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <Typography color="blueOne">
                                                {startCount} - {endCount} of {qmrTotal}
                                            </Typography>
                                            <Button
                                                iconRight={<Icon name="chevron-right" size={16} />}
                                                onPress={() => {
                                                    navigate('/qmrs');
                                                }}
                                                variant="tertiary"
                                            >
                                                View all QMRs
                                            </Button>
                                            <div></div>
                                        </div>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </DetailViewBody>
                </DetailViewContent>
            </DetailView>
        </AsyncPage>
    );
};

export default Overview;
